/* Event-driven shrink + fly-up (homepage hero logo) */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  const overlay = document.querySelector('.hero-logo-overlay');
  if (!overlay) return;

  // Prefer the <img> path (we hide the mask in CSS)
  const img =
    overlay.querySelector('.hero-logo-overlay__img') ||
    overlay.querySelector('.hero-logo-overlay__mask');
  if (!img) return;

  // ----- Knobs you can tweak -----
  const DURATION_MS = 1500; // time for each play/reverse
  const TARGET_HEIGHT = 24; // px when "small"
  const Y_EXIT_VH = 10; // how far up it flies by the end
  const TRIGGER_DOWN = 12; // px scrolled before we play (from top)
  const TOP_THRESHOLD = 12; // px from top to trigger reverse

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  // Runtime state
  let smallScale = 0.4; // will be measured
  let curScale = 1,
    curYvh = 0; // current values (we own them)
  let goal = 'expanded'; // 'expanded' | 'compact'
  let raf = 0;
  let anim = null; // {fromScale, toScale, fromY, toY, start, dur}
  let lastY = window.scrollY || 0;

  function setVars(scale, yvh, opacity) {
    curScale = scale;
    curYvh = yvh;
    overlay.style.setProperty('--scale', String(scale));
    overlay.style.setProperty('--y', `-${yvh}vh`);
    if (opacity != null) overlay.style.opacity = String(opacity);
  }

  function measure() {
    // Compute scale so the rendered height lands near TARGET_HEIGHT
    const rect = img.getBoundingClientRect();
    const h = rect.height || 1;
    smallScale = Math.min(1, TARGET_HEIGHT / h);
  }

  function play(toCompact) {
    if (
      (toCompact && goal === 'compact') ||
      (!toCompact && goal === 'expanded')
    )
      return;

    cancelAnimationFrame(raf);
    measure(); // re-measure right before anim (layout is settled on scroll)

    // Start from *current* values, go to the goal
    anim = {
      fromScale: curScale,
      toScale: toCompact ? smallScale : 1,
      fromY: curYvh,
      toY: toCompact ? Y_EXIT_VH : 0,
      start: performance.now(),
      dur: DURATION_MS,
      toOpacity: toCompact ? 0 : 1,
    };
    goal = toCompact ? 'compact' : 'expanded';

    const step = (now) => {
      const t = Math.min(1, (now - anim.start) / anim.dur);
      const e = easeOutCubic(t);

      const s = anim.fromScale + (anim.toScale - anim.fromScale) * e;
      const y = anim.fromY + (anim.toY - anim.fromY) * e;

      // Fade only near the end when compacting (nice little polish)
      let op = 1;
      if (toCompact) {
        const fadeStart = 0.85;
        op = t < fadeStart ? 1 : 1 - (t - fadeStart) / (1 - fadeStart);
      }

      setVars(s, y, op);

      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        // Snap to exact end values
        setVars(anim.toScale, anim.toY, toCompact ? 0 : 1);
        anim = null;
      }
    };

    raf = requestAnimationFrame(step);
  }

  function onScroll() {
    const y = window.scrollY || document.documentElement.scrollTop || 0;

    // Start the shrink as soon as we leave the top a tiny bit
    if (y > TRIGGER_DOWN && goal !== 'compact') {
      play(true);
    }

    // When we return to (near) the very top, play the reverse
    if (y <= TOP_THRESHOLD && goal !== 'expanded') {
      play(false);
    }

    lastY = y;
  }

  // Init: ensure vars exist and pick a correct starting state if we load mid-page
  setVars(1, 0, 1);
  if ((window.scrollY || 0) > TRIGGER_DOWN) {
    // If we land already scrolled, go straight to compact
    play(true);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
    },
    { passive: true },
  );

  // If the image wasn't ready yet, re-measure once it is
  if (img instanceof HTMLImageElement && !img.complete) {
    img.addEventListener(
      'load',
      () => {
        measure();
      },
      { once: true },
    );
  }
})();
